/**
 * metaLeadGen/genderBreakdown.ts
 *
 * Aggregates Meta rows by gender. Shows only real Meta data (spend, optins, clicks).
 * Application/close attribution by gender will be added once UTM tracking is in place.
 */

import type { MetaLeadGenRow, MetaLeadGenFilters } from "./types";
import { applyFilters } from "./filters";
import { safeDivide } from "./calculations";

// ─────────────────────────────────────────────
// GENDER CONSTANTS
// ─────────────────────────────────────────────
export const GENDERS = ["male", "female", "unknown"] as const;
export type GenderKey = (typeof GENDERS)[number];

export const GENDER_LABELS: Record<GenderKey, string> = {
  male:    "Male",
  female:  "Female",
  unknown: "Unknown",
};

// ─────────────────────────────────────────────
// OUTPUT TYPES
// ─────────────────────────────────────────────
export interface GenderSummaryRow {
  gender: GenderKey;
  label: string;         // "Male" | "Female" | "Unknown"
  spend: number;
  optins: number;
  costPerOptin: number;  // spend / optins (0 if optins = 0)
}

/**
 * One row per date in the filtered range.
 * Flat keys so Recharts Line components can use direct dataKey strings.
 */
export interface GenderDailyRow {
  date: string;            // display format e.g. "Jan 13"
  male_spend: number;    female_spend: number;    unknown_spend: number;
  male_clicks: number;   female_clicks: number;   unknown_clicks: number;
  male_optins: number;   female_optins: number;   unknown_optins: number;
}

// ─────────────────────────────────────────────
// INTERNAL HELPERS
// ─────────────────────────────────────────────
function toDisplayDate(isoDate: string): string {
  const d = new Date(isoDate + "T00:00:00Z");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function normalizeGender(raw: string): GenderKey {
  return (GENDERS as readonly string[]).includes(raw) ? (raw as GenderKey) : "unknown";
}

type GenderAcc = { spend: number; leads: number; clicks: number };
type GenderAccMap = Record<GenderKey, GenderAcc>;

function emptyAccMap(): GenderAccMap {
  return {
    male:    { spend: 0, leads: 0, clicks: 0 },
    female:  { spend: 0, leads: 0, clicks: 0 },
    unknown: { spend: 0, leads: 0, clicks: 0 },
  };
}

function deriveRow(acc: GenderAccMap): Omit<GenderDailyRow, "date"> {
  return {
    male_spend:    acc.male.spend,    female_spend:    acc.female.spend,    unknown_spend:    acc.unknown.spend,
    male_clicks:   acc.male.clicks,   female_clicks:   acc.female.clicks,   unknown_clicks:   acc.unknown.clicks,
    male_optins:   acc.male.leads,    female_optins:   acc.female.leads,    unknown_optins:   acc.unknown.leads,
  };
}

// ─────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────

/**
 * Returns one GenderSummaryRow per canonical gender.
 * Respects all active filters.
 */
export function getGenderSummary(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters
): GenderSummaryRow[] {
  const filtered = applyFilters(rows, filters);
  const agg = emptyAccMap();

  for (const row of filtered) {
    const g = normalizeGender(row.gender);
    agg[g].spend  += row.spend;
    agg[g].leads  += row.leads;
    agg[g].clicks += row.clicks;
  }

  return GENDERS.map(gender => {
    const { spend, leads: optins } = agg[gender];
    return {
      gender,
      label:        GENDER_LABELS[gender],
      spend,
      optins,
      costPerOptin: safeDivide(spend, optins),
    };
  });
}

/**
 * Returns one GenderDailyRow per date present in the filtered rows.
 * Dates are in ascending order. Display format used ("Jan 13").
 */
export function getGenderDailyData(
  rows: MetaLeadGenRow[],
  filters: MetaLeadGenFilters
): GenderDailyRow[] {
  const filtered = applyFilters(rows, filters);

  const dateSet = new Set<string>();
  for (const row of filtered) dateSet.add(row.date);
  const dates = Array.from(dateSet).sort();

  const byDate = new Map<string, GenderAccMap>();
  for (const date of dates) byDate.set(date, emptyAccMap());

  for (const row of filtered) {
    const g   = normalizeGender(row.gender);
    const acc = byDate.get(row.date)!;
    acc[g].spend  += row.spend;
    acc[g].leads  += row.leads;
    acc[g].clicks += row.clicks;
  }

  return dates.map(date => ({
    date: toDisplayDate(date),
    ...deriveRow(byDate.get(date)!),
  }));
}
